import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Navbar from '../../Navbar';
import PageTitle from '../../components/PageTitle';
import RichTextEditor from '../../components/RichTextEditor';
import ShareGuideModal from './ShareGuideModal';
import PublishGuideModal from './PublishGuideModal';
import GuideOutlineSidebar from './GuideOutlineSidebar';
import GuideTitleEditor from './GuideTitleEditor';
import {
  authHeaders,
  extractHeadingsFromHtml,
  stampGuideHeadingIds,
  parseGuideTitleHtml,
  splitGuideDocumentHtml,
  composeGuideDocumentHtml,
  guidesFetch,
  stripGuidePlaceholdersInPlace,
  insertGuideSection,
  removeGuideSection,
  moveGuideSection
} from './guidesUtils';
import { GuideAutosaveIndicator, useGuideDocumentAutosave } from './guideAutosave';
import { printGuideDocument } from './printGuideDocument';
import './guides.css';

export default function ArticleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [contentHtml, setContentHtml] = useState('');
  const [titleHtml, setTitleHtml] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [outlineToggleHost, setOutlineToggleHost] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await guidesFetch(`/articles/${id}`, { headers: authHeaders() });
      if (!data.canEdit) {
        navigate(`/guides/articles/${id}/read`, { replace: true });
        return;
      }
      setArticle(data);
      const split = splitGuideDocumentHtml(data.contentHtml || '', { fallbackTitle: data.title || '' });
      setTitle(split.titleText || data.title || '');
      setTitleHtml(split.titleHtml);
      setContentHtml(extractHeadingsFromHtml(split.bodyHtml).htmlWithIds);
    } catch (err) {
      toast.error(err.message || 'Failed to load article');
      navigate('/guides/mine');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const { headings, topicHeadingLevel } = useMemo(
    () => extractHeadingsFromHtml(contentHtml),
    [contentHtml]
  );

  useLayoutEffect(() => {
    const root = document.querySelector('.guides-topic-editor-col .rte-editable-root');
    if (!stampGuideHeadingIds(root, headings)) return;
    const next = root.innerHTML;
    setContentHtml((current) => (current === next ? current : next));
  }, [headings]);
  const titleParsed = useMemo(() => parseGuideTitleHtml(titleHtml), [titleHtml]);
  const composedHtml = useMemo(
    () => composeGuideDocumentHtml(titleHtml, contentHtml),
    [titleHtml, contentHtml]
  );

  const scrollToHeading = (headingId) => {
    const safeId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(headingId) : headingId;
    const el = document.querySelector(`.guides-topic-editor-col [id="${safeId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const applyContent = (nextHtml, scrollId = null) => {
    const { htmlWithIds } = extractHeadingsFromHtml(nextHtml);
    setContentHtml(htmlWithIds);
    if (scrollId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToHeading(scrollId));
      });
    }
  };

  const titleRef = useRef(title);
  titleRef.current = title;
  const articleIdRef = useRef(id);
  articleIdRef.current = id;

  const persist = useCallback(async (html) => {
    const saveId = id;
    try {
      const split = splitGuideDocumentHtml(html, { fallbackTitle: titleRef.current });
      const { htmlWithIds } = extractHeadingsFromHtml(split.bodyHtml);
      const resolvedTitle = split.titleText || String(titleRef.current || '').trim();
      const composed = composeGuideDocumentHtml(split.titleHtml, htmlWithIds);
      const updated = await guidesFetch(`/articles/${saveId}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ title: resolvedTitle || titleRef.current, contentHtml: composed })
      });
      if (String(articleIdRef.current) !== String(saveId)) return { html };
      setArticle(updated);
      // Do not write the saved HTML back into the editors. That string is a
      // reparsed copy, and it can be older than keystrokes made while the
      // request was in flight. Either one replaces the document and moves the cursor.
      return { html };
    } catch (err) {
      toast.error(err.message || 'Failed to save');
      throw err;
    }
  }, [id]);

  const { status: saveStatus, savedAt, saveNow } = useGuideDocumentAutosave({
    html: composedHtml,
    enabled: !loading,
    resetKey: id,
    persist
  });

  const save = async () => {
    try {
      await saveNow();
      toast.success('Saved');
    } catch {
      /* persist already toasted */
    }
  };

  const togglePublish = async () => {
    if (!article.published) {
      setPublishOpen(true);
      return;
    }
    if (!window.confirm('Unpublish this article?')) return;
    try {
      const res = await guidesFetch(`/articles/${id}/publish`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ published: false })
      });
      setArticle((a) => ({
        ...a,
        published: res.published,
        publishedAt: res.publishedAt,
        allowEdits: res.allowEdits,
        categoryId: res.categoryId,
        topicId: res.topicId,
        description: res.description ?? a.description
      }));
      toast.success('Unpublished');
    } catch (err) {
      toast.error(err.message || 'Failed to update publish status');
    }
  };

  const handleTitleChange = (html) => {
    setTitleHtml(html);
    setTitle(parseGuideTitleHtml(html).text);
  };

  const handleAddTopic = (name, afterHeadingId) => {
    const { html, id: newId } = insertGuideSection(contentHtml, {
      text: name,
      outlineLevel: 1,
      afterHeadingId,
      topicHeadingLevel
    });
    applyContent(html, newId);
  };

  const handleAddSubtopic = (name, afterHeadingId) => {
    const { html, id: newId } = insertGuideSection(contentHtml, {
      text: name,
      outlineLevel: 2,
      afterHeadingId: afterHeadingId || headings.find((h) => h.outlineLevel === 1)?.id || null,
      topicHeadingLevel
    });
    applyContent(html, newId);
  };

  const handleAddLowerSubtopic = (name, afterHeadingId) => {
    const { html, id: newId } = insertGuideSection(contentHtml, {
      text: name,
      outlineLevel: 3,
      afterHeadingId,
      topicHeadingLevel
    });
    applyContent(html, newId);
  };

  const handleRemoveSection = (headingId) => {
    applyContent(removeGuideSection(contentHtml, headingId, topicHeadingLevel));
  };

  const handleMoveSection = (sourceId, targetId) => {
    const next = moveGuideSection(contentHtml, sourceId, targetId, topicHeadingLevel);
    if (next !== contentHtml) applyContent(next, sourceId);
  };

  if (loading || !article) {
    return (
      <div className="guides-app guides-loading-screen">
        <Navbar pageTitle="Edit article" />
        <div className="guides-loading"><Spinner /></div>
      </div>
    );
  }

  const displayTitle = title || 'Edit article';

  return (
    <div className="guides-app guides-topic-editor-page bg-inverse">
      <Navbar pageTitle={displayTitle} />
      <PageTitle title={displayTitle} />
      <div className="bg-white border-bottom guides-topic-editor-topbar">
        <Container fluid="lg">
          <div className="d-flex justify-content-between align-items-start gap-3 py-2 flex-wrap">
            <div className="d-flex align-items-start gap-3 min-w-0">
              <div ref={setOutlineToggleHost} className="d-flex d-md-none" />
              <Button size="sm" variant="light-solid" className="guides-icon-btn mt-1 d-none d-md-inline-flex" onClick={() => navigate('/guides/mine')}>
                <i className="bi bi-chevron-left" />
              </Button>
              <div className="min-w-0">
                <h6 className="mb-0 fw-semibold">{displayTitle}</h6>
                <div className="small mt-0 text-muted">Article</div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <div className="radio-inputs guides-topbar-radios">
                <label className="radio">
                  <input
                    type="checkbox"
                    checked={!!article.published}
                    onChange={togglePublish}
                    aria-label={article.published ? 'Published' : 'Publish'}
                  />
                  <span className="name px-3">
                    <i className="bi bi-globe2" aria-hidden />
                    <span className="d-none d-md-inline ms-1">{article.published ? 'Published' : 'Publish'}</span>
                  </span>
                </label>
                <label className="radio">
                  <input
                    type="checkbox"
                    checked={(article.sharedUserIds || []).length > 0}
                    onChange={() => setShareOpen(true)}
                    aria-label="Shared"
                  />
                  <span className="name px-3">
                    <i className="bi bi-share" aria-hidden />
                    <span className="d-none d-md-inline ms-1">Shared</span>
                  </span>
                </label>
                <label className="radio">
                  <input
                    type="button"
                    onClick={() => navigate(`/guides/articles/${id}/read`)}
                    aria-label="Read"
                  />
                  <span className="name bg-white-hover px-3">
                    <i className="bi bi-book" aria-hidden />
                    <span className="d-none d-md-inline ms-1">Read</span>
                  </span>
                </label>
              </div>
              <GuideAutosaveIndicator status={saveStatus} savedAt={savedAt} />
              <Button
                size="sm"
                variant="light-solid"
                onClick={() => window.dispatchEvent(new CustomEvent('guides-reference-library', {
                  detail: { targetType: 'article', targetId: Number(id) }
                }))}
              >
                <i className="bi bi-journal-text me-1" />
                References
              </Button>
              <Button
                size="sm"
                variant="light-solid"
                onClick={() => printGuideDocument({ title: displayTitle })}
                title="Print"
              >
                <i className="bi bi-printer me-1" />
                Print
              </Button>
              <Button size="sm" variant="outline-success" onClick={save} disabled={saveStatus === 'saving'}>
                <i className="bi bi-floppy me-1" />
                {saveStatus === 'saving' ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container fluid="lg" className="guides-topic-editor-workspace">
        <Row className="g-0 h-100">
          <Col lg={2} md={3} className="border-end guides-topic-editor-rail bg-inverse d-none d-md-block">
            <GuideOutlineSidebar
              headings={headings}
              titleFallback={title}
              titleEmpty={titleParsed.empty}
              topicHeadingLevel={topicHeadingLevel}
              onScrollTo={scrollToHeading}
              onAddTopic={handleAddTopic}
              onAddSubtopic={handleAddSubtopic}
              onAddLowerSubtopic={handleAddLowerSubtopic}
              onRemoveSection={handleRemoveSection}
              onMoveSection={handleMoveSection}
              mobileToggleHost={outlineToggleHost}
            />
          </Col>
          <Col lg={10} md={9} className="guides-topic-editor-col border-end">
            <RichTextEditor
              value={contentHtml}
              onChange={setContentHtml}
              prepareDomBeforeEmit={stripGuidePlaceholdersInPlace}
              placeholder="Write your article…"
              variant="document"
              references={{
                targetType: 'article',
                targetId: Number(id),
                programId: article.programId
              }}
              documentHeader={(
                <GuideTitleEditor
                  value={titleHtml}
                  onChange={handleTitleChange}
                  onRequestFocusBody={() => {
                    document.querySelector('.guides-topic-editor-col .rte-editable-root')?.focus();
                  }}
                />
              )}
            />
          </Col>
        </Row>
      </Container>

      <ShareGuideModal
        show={shareOpen}
        onHide={() => setShareOpen(false)}
        targetType="article"
        targetId={Number(id)}
        initialUserIds={article.sharedUserIds || []}
        onSaved={(shares) => setArticle((a) => ({
          ...a,
          sharedUserIds: shares.map((s) => s.userId)
        }))}
      />
      <PublishGuideModal
        show={publishOpen}
        onHide={() => setPublishOpen(false)}
        targetType="article"
        targetId={Number(id)}
        initialDescription={article.description || ''}
        initialCategoryId={article.categoryId}
        initialTopicId={article.topicId}
        initialAllowEdits={!!article.allowEdits}
        onPublished={(res) => {
          setArticle((a) => ({
            ...a,
            published: true,
            publishedAt: res.publishedAt,
            allowEdits: res.allowEdits,
            categoryId: res.categoryId,
            topicId: res.topicId,
            description: res.description ?? a.description,
            category: res.categoryName || a.category
          }));
        }}
      />
    </div>
  );
}
